import React from 'react';
import { motion } from 'framer-motion';
import { integrations } from '../constants';
import { check } from '../assets';

const cardVariants = {
  initial: { y: 20, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  hover: { 
    y: -5,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  }
};

const IntegrationCard = ({ integration, index }) => {
  return (
    <motion.div
      variants={cardVariants}
      initial="initial"
      whileInView="animate"
      whileHover="hover"
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative group"
    >              
      <div className="relative h-full">
        {/* Card background */}
        <div className="absolute inset-0 bg-[#1B1B1D] dark:bg-[#0A0F1C] rounded-2xl">
          <div className="absolute inset-0 bg-gradient-to-b from-white/[0.04] to-transparent rounded-2xl" />
        </div>
        
        {/* Card content */}
        <div className="relative p-5 rounded-2xl h-full flex flex-col">
          <div className="flex items-start gap-3 mb-4">
            {/* Icon container */}
            <div className="relative flex-shrink-0">
              <div className="w-10 h-10 bg-[#313134] dark:bg-gray-800 rounded-xl p-2 border border-white/[0.08]">
                <img src={integration.iconUrl} alt={integration.title} className="w-full h-full" />
              </div>
            </div>
            
            {/* Title and description */}
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold mb-1 text-white truncate">
                {integration.title}
              </h3>
              <p className="text-xs text-gray-400 line-clamp-2">
                {integration.text}
              </p>
            </div>
          </div>

          {/* Features list */}
          <div className="space-y-2 mb-4">
            {integration.features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center gap-2"
              >
                <div className="w-4 h-4 rounded-full bg-[#313134] dark:bg-gray-800 flex items-center justify-center p-0.5">
                  <img src={check} alt="Check" className="w-full h-full opacity-70" />
                </div>
                <span className="text-xs text-gray-400 truncate">
                  {feature}
                </span>
              </motion.div>
            ))}
          </div>

          {/* Connect button */}
          <div className="mt-auto">
            <button className="w-full py-2 text-xs font-medium text-white bg-[#313134] dark:bg-gray-800 rounded-lg border border-white/[0.08] hover:bg-[#3B3B3E] dark:hover:bg-gray-700 transition-colors duration-300">
              Connect
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Integrations = () => {
  return (
    <section className="relative w-full py-20">
      {/* Background gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-48 right-0 w-[800px] h-[800px] bg-radial-gradient from-[#8B5CF6]/20 via-transparent to-transparent opacity-50 mix-blend-overlay" />
        <div className="absolute -bottom-48 left-0 w-[800px] h-[800px] bg-radial-gradient from-[#3B82F6]/20 via-transparent to-transparent opacity-50 mix-blend-overlay" />
      </div>

      <div className="container relative z-2 mx-auto px-4">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="relative inline-block">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/40 to-transparent blur-xl"
              />
              <h2 className="text-5xl md:text-6xl font-bold mb-4 relative">
                Powerful <span className="gradient-text-blue">Integrations</span>
              </h2>
            </div>
            <p className="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto">
              Connect your favorite tools and platforms with our AI-powered integrations.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {integrations.map((integration, index) => (
            <IntegrationCard key={integration.id} integration={integration} index={index} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-gray-500 dark:text-gray-400 mb-4">Need a custom integration?</p>
          <button className="button button-secondary">
            Contact Our Team
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Integrations;
